import { useCallback, useEffect, useRef, useState } from "react";

export const useDebounce = <T,>(value: T, timer?: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, timer || 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, timer]);

  return debouncedValue;
};

export const useDebouncedCallback = <T extends (...args: never[]) => void>(
  callback: T,
  delay: number = 500,
) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );
};
